import { useState } from 'react';
import styled  from 'styled-components';
import { breakpoints } from '@/layouts/breakpoints'
import { ChevronDown } from 'lucide-react';
import JsonLd from '@/components/JsonLd';
import { buildFaqPage } from '@/lib/schema';
import { SUPPORT_FAQS, type FaqEntry } from '@/lib/faqs';
import { FREE_TRIAL_ENABLED } from '@/lib/features';

const TRIAL_FAQ: FaqEntry = {
  question: 'Is there a free trial?',
  answer:
    'Yes. Every new account includes a 14-day free Pro trial with full access to all API endpoints and features. No charges are applied during the trial, giving you time to explore the platform and evaluate whether it meets your needs before choosing a paid plan.',
};

const DEFAULT_FAQS: FaqEntry[] = FREE_TRIAL_ENABLED
  ? [...SUPPORT_FAQS.slice(0, 4), TRIAL_FAQ, ...SUPPORT_FAQS.slice(4)]
  : SUPPORT_FAQS;

interface FAQProps {
  /** Override Q&As (e.g. per-product sets). Defaults to the site-wide FAQs. */
  faqs?: FaqEntry[];
  /** Emit FAQPage JSON-LD for this instance. */
  schema?: boolean;
}


const StyledComp = styled.div`
  padding: 80px 40px;
  max-width: 1000px;
  margin: 0 auto;
  border-bottom: 1px solid rgba(var(--greyr), 0.3);

  @media (max-width: ${breakpoints.medium}) {
    padding: 40px 20px;
  }

  @media (max-width: ${breakpoints.xsmall}) {
    padding: 32px 16px;
  }

  .faq-header {
    text-align: center;
    margin-bottom: 64px;

    @media (max-width: ${breakpoints.xsmall}) {
      margin-bottom: 40px;
    }

    .faq-eyebrow {
      display: inline-block;
      font-size: 12px;
      letter-spacing: 3px;
      text-transform: uppercase;
      margin-bottom: 18px;
      color: var(--black);
    }

    h2 {
      font-size: 48px;
      margin-bottom: 16px;

      em {
        font-style: italic;
        color: var(--grey);
      }

      @media (max-width: ${breakpoints.medium}) {
        font-size: 36px;
      }

      @media (max-width: ${breakpoints.xsmall}) {
        font-size: 28px;
        margin-bottom: 12px;
      }
    }

    p {
      font-size: 15px;
      color: var(--grey);

      @media (max-width: ${breakpoints.xsmall}) {
        font-size: 13px;
      }
    }
  }

  .faq-list {
    display: flex;
    flex-direction: column;
    gap: 0;

    .faq-item {
      border-bottom: 1px solid rgba(var(--greyr), 0.18);
      transition: background-color 0.35s ease;

      &:last-child {
        border-bottom: none;
      }

      &:hover,
      &.open { }
    }

    .faq-question {
      position: relative;
      display: flex;
      align-items: center;
      gap: 24px;
      width: 100%;
      padding: 28px 4px;
      background: none;
      border: none;
      margin: 0;
      font: inherit;
      color: inherit;
      text-align: left;
      cursor: pointer;
      -webkit-appearance: none;
      appearance: none;

      &:focus-visible {
        outline-offset: 4px;
        border-radius: 8px;
      }

      .faq-number {
        flex-shrink: 0;
        min-width: 36px;
        font-size: 22px;
        font-style: italic;
        color: #595959;
        transition: color 0.35s ease;

        @media (max-width: ${breakpoints.xsmall}) {
          display: none;
        }
      }

      h3 {
        flex: 1;
        font-size: 17px;
        letter-spacing: 0.5px;
        line-height: 1.4;
        transition: color 0.3s ease;

        @media (max-width: ${breakpoints.xsmall}) {
          font-size: 14px;
        }
      }

      .faq-icon {
        flex-shrink: 0;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid rgba(var(--greyr), 0.25);
        color: var(--grey);
        transition: background-color 0.35s ease, border-color 0.35s ease, color 0.35s ease, transform 0.35s ease;

        svg {
          transition: transform 0.35s ease;
        }

        @media (max-width: ${breakpoints.xsmall}) {
          width: 30px;
          height: 30px;
        }
      }

      &:hover {
        .faq-number {
          color: var(--black);
        }

        .faq-icon {
          border-color: var(--black);
          color: var(--black);
        }
      }
    }

    .faq-item.open .faq-question {

      .faq-number {
        color: var(--black);
      }

      h3 {
        color: var(--black);
      }

      .faq-icon {
        background: var(--black);
        border-color: var(--black);
        color: var(--white);
        transform: rotate(180deg);
      }
    }

    .faq-answer-wrapper {
      display: grid;
      grid-template-rows: 0fr;
      overflow: hidden;
      transition: grid-template-rows 0.45s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .faq-item.open .faq-answer-wrapper {
      grid-template-rows: 1fr;
    }

    .faq-answer {
      min-height: 0;
      overflow: hidden;

      p {
        padding: 0 56px 28px 60px;
        font-size: 15px;
        line-height: 1.7;
        color: var(--grey);
        max-width: 70ch;
        opacity: 0;
        transition: opacity 0.3s ease;

        @media (max-width: ${breakpoints.xsmall}) {
          padding: 0 8px 24px 4px;
          font-size: 13px;
        }
      }
    }

    .faq-item.open .faq-answer p {
      opacity: 1;
      transition: opacity 0.4s ease 0.15s;
    }
  }

`;


const Comp = ({ faqs = DEFAULT_FAQS, schema = false }: FAQProps) => {

  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <StyledComp>
      {schema && <JsonLd data={buildFaqPage(faqs)} />}
      <div className="faq-header">
        <h2 className="serif">Frequently Asked <em>Questions</em></h2>
      </div>

      <div className="faq-list">
        {faqs.map((faq, index) => (
          <div key={index} className={`faq-item ${openFaq === index ? 'open' : ''}`}>
            <button
              type="button"
              className="faq-question"
              onClick={() => toggleFaq(index)}
              aria-expanded={openFaq === index}
            >
              <span className="faq-number serif">{String(index + 1).padStart(2, '0')}</span>
              <h3 className="">{faq.question}</h3>
              <span className="faq-icon">
                <ChevronDown size={18} />
              </span>
            </button>
            <div className="faq-answer-wrapper">
              <div className="faq-answer">
                <p>{faq.answer}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </StyledComp>
  );
};

export default Comp;
